// ** React Imports
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

// ** Third Party Components
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";

// ** Nearby Components
import Users from "./components/Users";
import Posts from "./components/Posts";
import GalleryMasonry from "./components/GalleryMasonry";
import { NearbyFiltersSection } from "./components/NearbyFiltersSection";
import EmptyBlock from "components/layout/Block/BlockEmpty";
import { CardsBlockWrapper } from "components/layout/Card/CardsBlockWrapper";
import ScrollToTop from "components/global/ScrollToTop";

// ** Custom Hooks
import useProcess from "hooks/useProcess";
import useOpenGraphMetaTags from "hooks/useOpenGraphMetaTags";
import useQuery from "hooks/useQuery";
import { compute as filterSexCompute } from "hooks/filters/useFilterSex";

// ** Utils & Helpers
import generateQuery from "utils/generateQuery";
import api from "helpers/ApiClient";

// ** Contexts
import authContext from "contexts/authContext";

// #####################################################

const Nearby = () => {
	const { t } = useTranslation(["pages", "landing"]);

	const history = useHistory();
	const location = useLocation();
	const isAuth = useContext(authContext);
	const [locationKey, setLocationKey] = useState("");

	const query = useQuery();

	const country = query["country"] ?? "any";
	const regionId = query["region"] ?? undefined;
	const cityId = query["city"] ?? "any";
	const distance = query["distance"] ?? 50;

	const { loading, data, process, ready } = useProcess({
		initial: {
			users: [],
			media: [],
			posts: [],
			location: { city: "" },
		},
		promise: (params) => {
			const generatedQuery = generateQuery({
				country: country,
				region: regionId,
				city: cityId,
				distance: distance,
				sex:
					filterSexCompute(true).urlToApiParam(
						query["sex"] //"woman-man-couple-transsexual"
					) ||
					filterSexCompute(true).urlToApiParam(
						"woman-man-couple-transsexual"
					),
			});
			const url = `/datezone/nearby?${generatedQuery}`;
			return api.get(url, {
				params,
			});
		},
	});

	const cityName = data.location.city;
	const regionName = data.location.region;
	const countryCode = data.location.country;
	const searchUrl = data.location.url;

	const locationFull = useMemo(() => {
		return {
			country,
			countryName: countryCode,
			regionId,
			regionName,
			cityId,
			cityName,
			distance,
			url: searchUrl,
		};
	}, [country, regionId, regionName, cityId, cityName, distance, searchUrl]);

	const title = `${t("pages:nearby")} - ${t("common:titleTemplate")}`;

	useEffect(() => {
		if (location.state?.forceReload) {
			return;
		}
		// ** Sprawdzenie czy zalogowany
		// ** jeżeli nie przekierowujemy do strony głównej
		if (!isAuth) {
			history.replace("/");
			window.scrollTo(0, 0);
		} else if (
			location.pathname === "/nearby" &&
			locationKey !== location.key
		) {
			process();
			setLocationKey(location.key);
		}
	}, [
		isAuth,
		query.country,
		query.city,
		query.region,
		query.distance,
		query.sex,
		location.pathname,
	]);

	useEffect(() => {
		if (location.state?.forceReload) {
			history.replace({ ...location, state: null });
			if (!isAuth) {
				history.replace("/");
				window.scrollTo(0, 0);
			} else if (
				location.pathname === "/nearby" &&
				location.hash === "" &&
				locationKey !== location.key
			) {
				process();
				setLocationKey(location.key);
			}
		}
	}, [location.state?.forceReload]);

	// ** Open Graph Meta Tags
	const ogMetaTags = useOpenGraphMetaTags({
		customTitle: title,
	});

	// #####################################################

	return (
		<>
			{/* Sekcja HEAD */}
			<Helmet title={title} titleTemplate="%s">
				{ogMetaTags}
			</Helmet>

			<ScrollToTop />

			{/* Główna zawartość strony */}
			<CardsBlockWrapper>
				<NearbyFiltersSection
					cityName={cityName}
					regionName={regionName}
					countryCode={countryCode}
					distance={query["distance"]}
				/>
				<GalleryMasonry
					gallery={data.media}
					loading={loading}
					isReady={ready}
					locationSearch={location.search}
					locationFull={locationFull}
				/>
				<Users
					users={data.users}
					loading={loading}
					cityName={cityName}
					regionName={regionName}
					countryCode={countryCode}
					searchUrl={searchUrl}
				/>
				<Posts
					posts={data.posts}
					loading={loading}
					locationFull={locationFull}
				/>
				<EmptyBlock />
			</CardsBlockWrapper>
		</>
	);
};

export default Nearby;
